/**
 * Reservation of a record from the portal: the patron chooses where to pick it
 * up (their home library by default). A copy on the shelf is set aside for
 * them; otherwise they join the queue.
 */

import { useId, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2 } from "lucide-react";
import { portalFetch, usePatronAuth } from "../contexts/PatronAuthContext";

interface PickupLocation {
  id: string;
  name: string;
  city: string | null;
}

interface ReservePanelProps {
  recordId: string;
}

export function ReservePanel({ recordId }: ReservePanelProps) {
  const { isAuthenticated, patron } = usePatronAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const id = useId();

  const pickupLocations = useQuery({
    queryKey: ["portal", "pickup-locations"],
    queryFn: async (): Promise<PickupLocation[]> => {
      const response = await fetch("/api/portal/catalog/pickup-locations");
      if (!response.ok) throw new Error("Impossible de charger les bibliothèques de retrait.");
      return ((await response.json()) as { locations: PickupLocation[] }).locations;
    },
    enabled: isAuthenticated,
    staleTime: 60 * 60 * 1000,
  });

  const locations = pickupLocations.data ?? [];
  const defaultPickup =
    locations.find((l) => l.id === patron?.homeLocation.id)?.id ?? locations[0]?.id ?? "";
  const [chosenPickup, setChosenPickup] = useState<string | null>(null);
  const pickupId = chosenPickup ?? defaultPickup;

  const reserve = useMutation({
    mutationFn: async (): Promise<string> => {
      const response = await portalFetch(`/catalog/records/${recordId}/reserve`, {
        method: "POST",
        body: JSON.stringify(pickupId ? { pickupLocationId: pickupId } : {}),
      });
      const data = (await response.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
      };
      if (!response.ok) {
        throw new Error(
          response.status === 401
            ? "Votre session a expiré : reconnectez-vous."
            : (data.error ?? "La réservation a échoué. Réessayez plus tard.")
        );
      }
      return data.message ?? "Réservation enregistrée.";
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal", "record", recordId] });
      void queryClient.invalidateQueries({ queryKey: ["portal", "account"] });
    },
  });

  if (!isAuthenticated) {
    return (
      <Link
        to="/opac/login"
        state={{ from: location.pathname }}
        className="mt-4 block w-full rounded-lg bg-blue-600 py-2 text-center font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:focus:ring-offset-gray-800"
      >
        Se connecter pour réserver
      </Link>
    );
  }

  if (reserve.isSuccess) {
    return (
      <div role="status" className="mt-4 space-y-2">
        <p className="flex gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
          {reserve.data}
        </p>
        <Link
          to="/opac/account"
          className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          Voir mes réservations
        </Link>
      </div>
    );
  }

  return (
    <form
      className="mt-4 space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        reserve.mutate();
      }}
    >
      {locations.length > 1 && (
        <div>
          <label
            htmlFor={`${id}-pickup`}
            className="mb-1 block text-sm text-gray-700 dark:text-gray-300"
          >
            Retrait à
          </label>
          <select
            id={`${id}-pickup`}
            value={pickupId}
            onChange={(e) => {
              setChosenPickup(e.target.value);
            }}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.city ? `${l.name} (${l.city})` : l.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {locations.length === 1 && (
        <p className="text-sm text-gray-600 dark:text-gray-400">Retrait à {locations[0]?.name}</p>
      )}
      {pickupLocations.error && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Retrait à votre bibliothèque habituelle.
        </p>
      )}

      <button
        type="submit"
        disabled={reserve.isPending || pickupLocations.isLoading}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2 font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-wait disabled:opacity-70 dark:focus:ring-offset-gray-800"
      >
        {reserve.isPending && (
          <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
        )}
        {reserve.isPending ? "Réservation…" : "Réserver"}
      </button>

      {reserve.error && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {reserve.error.message}
        </p>
      )}
    </form>
  );
}
